import { Record, Set } from 'immutable';
import { isValid } from 'date-fns';
import { DataType, SelectionMode, VariableType } from '@mode-vegas/vegas';
import { getInitialFlagValue } from '@mode/shared/util-js';
import { DomainResult } from '@mode-switch/query';

import { CompiledFormula, Switch } from '@mode/shared/util-switch';
import {
  StateRecord,
  ReportTypes,
  DiscreteValueOptions,
  RangeValueOption,
  RangeValueOptions,
  RangeValueOptionRecord,
  FeatureFlag,
} from '@mode/shared/contract-common';
import { DataRole, DateTime } from '@mode-switch/express';

interface IReportFilterControlRecord extends StateRecord {
  name: string;
  position: number;
  formula: string;
  dataType: DataType;
  formulaType: DataType;
  varType: VariableType;
  filterType: ReportTypes.ReportFilterType;
  hideSelectAll: boolean;
  showRelevantValuesOnly: boolean;
}

/**
 * Temporary workaround type to support preformatted options for report filters
 */
export type ReportFilterDomainResult = DomainResult<DataRole> & {
  readonly labels?: any[];
};

// Single Value Filter

export interface SingleValueSelectionRecord {
  all: boolean;
  value: ReportTypes.ReportFilterValue | undefined;
}

const DefaultSingleValueSelection: SingleValueSelectionRecord = {
  all: true,
  value: null,
};

export class SingleValueSelection extends Record<SingleValueSelectionRecord>(DefaultSingleValueSelection) {
  constructor(params?: Partial<SingleValueSelectionRecord>) {
    params ? super(params) : super();
  }

  with(values: Partial<SingleValueSelectionRecord>): SingleValueSelection {
    return this.merge(values);
  }
}

export interface SingleValueFilterControlRecord extends IReportFilterControlRecord {
  filterType: ReportTypes.ReportFilterType.Single; // TODO - Validate - Wouldn't this always be 'string'?
  controlType: ReportTypes.SelectControlType;

  selected: SingleValueSelection;
  default: SingleValueSelection;
  options: DiscreteValueOptions;
}

export interface SingleValueFilterControlJs extends IReportFilterControlRecord {
  filterType: ReportTypes.ReportFilterType.Single; // TODO - Validate - Wouldn't this always be 'string'?
  controlType: ReportTypes.SelectControlType;

  selected: SingleValueSelectionRecord;
  default: SingleValueSelectionRecord;
}

const DefaultSingleValueFilterControlRecord: SingleValueFilterControlRecord = {
  id: null,
  token: '',
  name: '',
  position: null,
  formula: 'NULL',
  dataType: DataType.STRING,
  formulaType: DataType.STRING,
  varType: VariableType.DISCRETE,
  filterType: ReportTypes.ReportFilterType.Single,
  controlType: getInitialFlagValue(FeatureFlag.ReportFilterDropdownEnabled)
    ? ReportTypes.ReportFilterControlType.Dropdown
    : ReportTypes.ReportFilterControlType.List,
  hideSelectAll: false,
  showRelevantValuesOnly: false,

  default: new SingleValueSelection(),
  selected: new SingleValueSelection(),
  options: new DiscreteValueOptions(),
};

export class SingleValueFilterControl extends Record<SingleValueFilterControlRecord>(
  DefaultSingleValueFilterControlRecord
) {
  static fromJS(record: Partial<SingleValueFilterControlJs>): SingleValueFilterControl {
    return new SingleValueFilterControl({
      ...record,
      default: new SingleValueSelection({
        all: record.default.all,
        value: record.default.value,
      }),
      selected: new SingleValueSelection({
        all: record.selected.all,
        value: record.selected.value,
      }),
    });
  }

  constructor(params?: Partial<SingleValueFilterControl>) {
    params ? super(params) : super();
  }

  get isDiscrete(): boolean {
    return this.varType === VariableType.DISCRETE;
  }

  get isContinuous(): boolean {
    return this.varType === VariableType.CONTINUOUS;
  }

  get isDateType(): boolean {
    switch (this.formulaType) {
      case DataType.DATE:
      case DataType.DATETIME:
        return true;
      default:
        return false;
    }
  }

  get isDiscreteDateType(): boolean {
    switch (this.formulaType) {
      case DataType.DATEQTR:
      case DataType.DATEMONTH:
      case DataType.DATEWKDAY:
        return true;
      default:
        return false;
    }
  }

  get isStringType(): boolean {
    switch (this.formulaType) {
      case DataType.STRING:
        return true;
      default:
        return false;
    }
  }

  get isNumericType(): boolean {
    switch (this.formulaType) {
      case DataType.FLOAT:
      case DataType.INTEGER:
      case DataType.NUMBER:
        return true;
      default:
        return false;
    }
  }

  with(values: Partial<SingleValueFilterControlRecord>): SingleValueFilterControl {
    return this.merge(values);
  }
}

// Multi Value Filter

export interface MultiValueSelectionRecord {
  all: boolean;
  selectionMode: SelectionMode;
  values: Set<ReportTypes.ReportFilterValue>;
}

export interface MultiValueSelectionJs {
  all: boolean;
  values: ReadonlyArray<ReportTypes.ReportFilterValue>;
}

const DefaultMultiValueSelection: MultiValueSelectionRecord = {
  all: true,
  selectionMode: SelectionMode.ALL,
  values: Set<ReportTypes.ReportFilterValue>(),
};

export class MultiValueSelection extends Record<MultiValueSelectionRecord>(DefaultMultiValueSelection) {
  constructor(params?: Partial<MultiValueSelectionRecord>) {
    params ? super(params) : super();
  }

  with(values: Partial<MultiValueSelectionRecord>): MultiValueSelection {
    return this.merge(values);
  }
}

export interface MultiValueFilterControlRecord extends IReportFilterControlRecord {
  filterType: ReportTypes.ReportFilterType.Multi;
  controlType: ReportTypes.SelectControlType;

  selected: MultiValueSelection;
  default: MultiValueSelection;
  options: DiscreteValueOptions;
}

// Doesn't have any immutable references
export interface MultiValueFilterControlJs extends IReportFilterControlRecord {
  filterType: ReportTypes.ReportFilterType.Multi;
  controlType: ReportTypes.SelectControlType;

  selected: MultiValueSelectionJs;
  default: MultiValueSelectionJs;
}

const DefaultMultiValueFilterControlRecord: MultiValueFilterControlRecord = {
  id: null,
  token: '',
  name: '',
  position: null,
  formula: 'NULL',
  dataType: DataType.STRING,
  formulaType: DataType.STRING,
  varType: VariableType.DISCRETE,
  filterType: ReportTypes.ReportFilterType.Multi,
  controlType: getInitialFlagValue(FeatureFlag.ReportFilterDropdownEnabled)
    ? ReportTypes.ReportFilterControlType.Dropdown
    : ReportTypes.ReportFilterControlType.List,
  hideSelectAll: false,
  showRelevantValuesOnly: false,

  selected: new MultiValueSelection(),
  default: new MultiValueSelection(),
  options: new DiscreteValueOptions(),
};

export class MultiValueFilterControl extends Record<MultiValueFilterControlRecord>(
  DefaultMultiValueFilterControlRecord
) {
  // TODO the input type here isn't actually a MultiValueFilterControl, it's a JS representation
  static fromJS(record: Partial<MultiValueFilterControlJs>): MultiValueFilterControl {
    return new MultiValueFilterControl({
      ...record,
      default: new MultiValueSelection({
        all: record.default.all,
        values: Set<ReportTypes.ReportFilterValue>(record.default.values),
      }),

      selected: new MultiValueSelection({
        all: record.selected.all,
        values: Set<ReportTypes.ReportFilterValue>(record.selected.values),
      }),
    });
  }

  constructor(params?: Partial<MultiValueFilterControl>) {
    params ? super(params) : super();
  }

  get isDiscrete(): boolean {
    return this.varType === VariableType.DISCRETE;
  }

  get isContinuous(): boolean {
    return this.varType === VariableType.CONTINUOUS;
  }

  get isDateType(): boolean {
    switch (this.formulaType) {
      case DataType.DATE:
      case DataType.DATETIME:
        return true;
      default:
        return false;
    }
  }

  get isDiscreteDateType(): boolean {
    switch (this.formulaType) {
      case DataType.DATEQTR:
      case DataType.DATEMONTH:
      case DataType.DATEWKDAY:
        return true;
      default:
        return false;
    }
  }

  get isStringType(): boolean {
    switch (this.formulaType) {
      case DataType.STRING:
        return true;
      default:
        return false;
    }
  }

  get isNumericType(): boolean {
    switch (this.formulaType) {
      case DataType.FLOAT:
      case DataType.INTEGER:
      case DataType.NUMBER:
        return true;
      default:
        return false;
    }
  }

  with(values: Partial<MultiValueFilterControlRecord>): MultiValueFilterControl {
    return this.merge(values);
  }
}

// Range Value Filter

export interface RangeValueFilterControlRecord extends IReportFilterControlRecord {
  filterType: ReportTypes.ReportFilterType.Range;
  controlType: ReportTypes.RangeControlType;

  selected: RangeValueOption;
  default: RangeValueOption;
  includeNulls: boolean;

  options: RangeValueOptions;
}

export interface RangeValueFilterControlJs extends IReportFilterControlRecord {
  filterType: ReportTypes.ReportFilterType.Range;
  controlType: ReportTypes.RangeControlType;

  selected: RangeValueOptionRecord;
  default: RangeValueOptionRecord;
  includeNulls: boolean;
}

const DefaultRangeValueFilterControlRecord: RangeValueFilterControlRecord = {
  id: null,
  token: '',
  name: '',
  position: null,
  formula: 'NULL',
  dataType: DataType.NUMBER,
  formulaType: DataType.NUMBER,
  varType: VariableType.CONTINUOUS,
  filterType: ReportTypes.ReportFilterType.Range,
  controlType: ReportTypes.ReportFilterControlType.Range,
  hideSelectAll: false,
  showRelevantValuesOnly: false,

  selected: new RangeValueOption(),
  default: new RangeValueOption(),
  includeNulls: false,

  options: new RangeValueOptions(),
};

export class RangeValueFilterControl extends Record<RangeValueFilterControlRecord>(
  DefaultRangeValueFilterControlRecord
) {
  static fromJS(record: Partial<RangeValueFilterControlJs>): RangeValueFilterControl {
    return new RangeValueFilterControl({
      ...record,
      default: new RangeValueOption(record.default),
      selected: new RangeValueOption(record.selected),
    });
  }

  constructor(params?: Partial<RangeValueFilterControlRecord>) {
    params ? super(params) : super();
  }

  get isDiscrete(): boolean {
    return this.varType === VariableType.DISCRETE;
  }

  get isContinuous(): boolean {
    return this.varType === VariableType.CONTINUOUS;
  }

  get isDateType(): boolean {
    switch (this.formulaType) {
      case DataType.DATE:
      case DataType.DATETIME:
        return true;
      default:
        return false;
    }
  }

  get isDiscreteDateType(): boolean {
    switch (this.formulaType) {
      case DataType.DATEQTR:
      case DataType.DATEMONTH:
      case DataType.DATEWKDAY:
        return true;
      default:
        return false;
    }
  }

  get isStringType(): boolean {
    switch (this.formulaType) {
      case DataType.STRING:
        return true;
      default:
        return false;
    }
  }

  get isNumericType(): boolean {
    switch (this.formulaType) {
      case DataType.FLOAT:
      case DataType.INTEGER:
      case DataType.NUMBER:
        return true;
      default:
        return false;
    }
  }

  with(values: Partial<RangeValueFilterControlRecord>): RangeValueFilterControl {
    return this.merge(values);
  }

  get selectedAsDates(): RangeValueOption {
    const { min, max } = this.selected;

    const minDate = min === null ? null : new Date(min);
    const maxDate = max === null ? null : new Date(max);

    if (isValid(minDate)) {
      (minDate as Date).setUTCHours(0, 0, 0, 0); // set to start of day
    }

    if (isValid(maxDate)) {
      (maxDate as Date).setUTCHours(23, 59, 59, 999); // set last second of day
    }

    return this.selected.with({
      min: minDate && minDate.valueOf(),
      max: maxDate && maxDate.valueOf(),
    });
  }

  withDates(selected: RangeValueOption): RangeValueFilterControl {
    const maxDate = selected.max === null ? null : new Date(selected.max);
    return this.merge({ selected: selected.with({ max: maxDate && maxDate.valueOf() }) });
  }
}

export type ReportFilterControlRecord =
  | SingleValueFilterControlRecord
  | MultiValueFilterControlRecord
  | RangeValueFilterControlRecord;

export type ReportFilterControlJs = SingleValueFilterControlJs | MultiValueFilterControlJs | RangeValueFilterControlJs;

/**
 * Legacy due to relying on ImmutableJS.
 */
export type LegacyReportFilterControl = SingleValueFilterControl | MultiValueFilterControl | RangeValueFilterControl;

export class ReportFilterControlFactory {
  static buildFromResource(resource: ReportTypes.ReportFilterResource): LegacyReportFilterControl {
    switch (resource.filter_type) {
      case ReportTypes.ReportFilterType.Single:
        return new SingleValueFilterControl({
          id: resource.token,
          name: resource.name,
          position: resource.row_order,
          formula: resource.formula,
          dataType: resource.data_type,
          formulaType: resource.formula_type,
          varType: resource.variable_type,
          controlType: resource.control_type,
          hideSelectAll: resource.hide_select_all,
          showRelevantValuesOnly: resource.show_relevant_values_only,
          default: new SingleValueSelection({
            all: (resource.options.default || { all: true }).all,
            value: (resource.options.default || { value: null }).value,
          }),
          selected: new SingleValueSelection({
            all: (resource.options.default || { all: true }).all,
            value: (resource.options.default || { value: null }).value,
          }),
        });
      case ReportTypes.ReportFilterType.Multi:
        return new MultiValueFilterControl({
          id: resource.token,
          name: resource.name,
          position: resource.row_order,
          formula: resource.formula,
          dataType: resource.data_type,
          formulaType: resource.formula_type,
          varType: resource.variable_type,
          controlType: resource.control_type,
          hideSelectAll: resource.hide_select_all,
          showRelevantValuesOnly: resource.show_relevant_values_only,
          default: new MultiValueSelection({
            all: (resource.options.default || { all: true }).all,
            values: Set<ReportTypes.ReportFilterValue>((resource.options.default || { values: [] }).values),
          }),
          selected: new MultiValueSelection({
            all: (resource.options.default || { all: true }).all,
            values: Set<ReportTypes.ReportFilterValue>((resource.options.default || { values: [] }).values),
          }),
        });
      case ReportTypes.ReportFilterType.Range:
        return new RangeValueFilterControl({
          id: resource.token,
          name: resource.name,
          position: resource.row_order,
          formula: resource.formula,
          dataType: resource.data_type,
          formulaType: resource.formula_type,
          varType: resource.variable_type,
          controlType: resource.control_type,
          default: new RangeValueOption(resource.options.default),
          selected: new RangeValueOption(resource.options.default),
        });
    }
  }

  static buildDefaultSelectControl(field: CompiledFormula): MultiValueFilterControl {
    const formula = `[${field.fieldName}]`;

    return new MultiValueFilterControl({
      id: `${field.attrDataType}:${formula}`,
      name: field.fieldName,
      formula,
      dataType: Switch.Compat.makeDataType(field.attrDataType),
      formulaType: Switch.Compat.makeDataType(field.formulaDataType),
      varType: VariableType.DISCRETE,
    });
  }

  static buildDefaultRangeControl(field: CompiledFormula): RangeValueFilterControl {
    const formula = `[${field.fieldName}]`;

    return new RangeValueFilterControl({
      id: `${Switch.Compat.makeDataType(field.attrDataType)}:${formula}`,
      name: field.fieldName,
      formula,
      dataType: Switch.Compat.makeDataType(field.attrDataType),
      formulaType: Switch.Compat.makeDataType(field.formulaDataType),
      varType: VariableType.CONTINUOUS,
    });
  }

  static buildDefaultDateControl(field: CompiledFormula): LegacyReportFilterControl {
    const formula = `[${field.fieldName}]`;

    return new RangeValueFilterControl({
      id: `${Switch.Compat.makeDataType(field.attrDataType)}:${formula}`,
      name: field.fieldName,
      formula,
      dataType: Switch.Compat.makeDataType(field.attrDataType),
      formulaType: Switch.Compat.makeDataType(field.formulaDataType),
    });
  }

  static buildDefaultYearControl(field: CompiledFormula): LegacyReportFilterControl {
    const formula = `DATEPART('${DateTime.Unit.Year}', [${field.fieldName}])`;

    return new MultiValueFilterControl({
      id: `${Switch.Compat.makeDataType(field.attrDataType)}:${formula}`,
      name: `Year (${field.fieldName})`,
      formula,
      dataType: Switch.Compat.makeDataType(field.attrDataType),
      formulaType: DataType.INTEGER,
    });
  }

  static buildDefaultQuarterControl(field: CompiledFormula): LegacyReportFilterControl {
    const formula = `DATEPART('${DateTime.Unit.Quarter}', [${field.fieldName}])`;

    return new MultiValueFilterControl({
      id: `${Switch.Compat.makeDataType(field.attrDataType)}:${formula}`,
      name: `Quarter (${field.fieldName})`,
      formula,
      dataType: Switch.Compat.makeDataType(field.attrDataType),
      formulaType: DataType.DATEQTR,
    });
  }

  static buildDefaultMonthControl(field: CompiledFormula): LegacyReportFilterControl {
    const formula = `DATEPART('${DateTime.Unit.Month}', [${field.fieldName}])`;

    return new MultiValueFilterControl({
      id: `${Switch.Compat.makeDataType(field.attrDataType)}:${formula}`,
      name: `Month (${field.fieldName})`,
      formula,
      dataType: Switch.Compat.makeDataType(field.attrDataType),
      formulaType: DataType.DATEMONTH,
    });
  }

  static buildDefaultDayControl(field: CompiledFormula): LegacyReportFilterControl {
    const formula = `DATEPART('${DateTime.Unit.Day}', [${field.fieldName}])`;

    return new MultiValueFilterControl({
      id: `${Switch.Compat.makeDataType(field.attrDataType)}:${formula}`,
      name: `Day of Month (${field.fieldName})`,
      formula,
      dataType: Switch.Compat.makeDataType(field.attrDataType),
      formulaType: DataType.INTEGER,
    });
  }

  static buildDefaultWeekControl(field: CompiledFormula): LegacyReportFilterControl {
    const formula = `DATEPART('${DateTime.Unit.Week}', [${field.fieldName}])`;

    return new MultiValueFilterControl({
      id: `${Switch.Compat.makeDataType(field.attrDataType)}:${formula}`,
      name: `Week of Year (${field.fieldName})`,
      formula,
      dataType: Switch.Compat.makeDataType(field.attrDataType),
      formulaType: DataType.INTEGER,
    });
  }

  static buildDefaultWeekdayControl(field: CompiledFormula): LegacyReportFilterControl {
    const formula = `DATEPART('${DateTime.Unit.Weekday}', [${field.fieldName}])`;

    return new MultiValueFilterControl({
      id: `${Switch.Compat.makeDataType(field.attrDataType)}:${formula}`,
      name: `Day of Week (${field.fieldName})`,
      formula,
      dataType: Switch.Compat.makeDataType(field.attrDataType),
      formulaType: DataType.DATEWKDAY,
    });
  }

  static buildDefaultHourControl(field: CompiledFormula): LegacyReportFilterControl {
    const formula = `DATEPART('${DateTime.Unit.Hour}', [${field.fieldName}])`;

    return new MultiValueFilterControl({
      id: `${Switch.Compat.makeDataType(field.attrDataType)}:${formula}`,
      name: `Hour (${field.fieldName})`,
      formula,
      dataType: Switch.Compat.makeDataType(field.attrDataType),
      formulaType: DataType.INTEGER,
    });
  }

  static buildDefaultMinuteControl(field: CompiledFormula): LegacyReportFilterControl {
    const formula = `DATEPART('${DateTime.Unit.Minute}', [${field.fieldName}])`;

    return new MultiValueFilterControl({
      id: `${Switch.Compat.makeDataType(field.attrDataType)}:${formula}`,
      name: `Minute (${field.fieldName})`,
      formula,
      dataType: Switch.Compat.makeDataType(field.attrDataType),
      formulaType: DataType.INTEGER,
    });
  }

  static buildDefaultSecondControl(field: CompiledFormula): LegacyReportFilterControl {
    const formula = `DATEPART('${DateTime.Unit.Second}', [${field.fieldName}])`;

    return new MultiValueFilterControl({
      id: `${Switch.Compat.makeDataType(field.attrDataType)}:${formula}`,
      name: `Second (${field.fieldName})`,
      formula,
      dataType: Switch.Compat.makeDataType(field.attrDataType),
      formulaType: DataType.INTEGER,
    });
  }
}
